import { all, put, call, select, getContext, takeLatest } from 'redux-saga/effects';
import { delay } from 'redux-saga';

import { Logger } from 'utils/Logger';
import { FundingMatchStatus } from 'enums/funding-match-status';

import { fundingMatchStatus, cancelFundingSearchMatch, SSE_UPDATE_FUNDING_MATCH } from './types';

import {
  closeCancelFundingSearchMatchModal,
  requestCancelFundingSearchMatchFailure,
  requestCancelFundingSearchMatchSuccess,
  sseUpdateFundingMatchSuccess,
  sseUpdateFundingMatchFailure,
  requestUpdateMatchStatusSuccess,
  requestUpdateMatchStatusFailure,
} from './reducer';
import { makeSelectFetchingOfferMatchIds } from '../selectors';
import { selectMatchIgnoreStatuses, selectMatchFromFundingSearch } from './selectors';
import { setIgnoredMatchStatuses, deleteFetchingOfferMatchId } from './actions';

const logger = new Logger('Saga:FundingSearchMatch');

const getState = (state) => state;

export function* requestCancelFundingSearchMatchSaga({ payload, promise }) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.put,
      routerService.generate('api.cancel_funding_match', {
        fundingMatchId: payload.matchId,
      }),
    );

    promise?.resolve && promise.resolve(response.data);

    yield put(requestCancelFundingSearchMatchSuccess(payload, response.data));
    yield put(closeCancelFundingSearchMatchModal());
  } catch (error) {
    logger.error('Failed to request cancel funding match with the following error', error);

    promise?.reject && promise.reject(error);

    yield put(requestCancelFundingSearchMatchFailure(error));
  }
}

export function* requestMatchUpdateSaga({ payload, storeState }) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  const targetMatch = selectMatchFromFundingSearch(storeState, payload);

  if (!targetMatch) {
    return;
  }

  const { status, fundingMatchId } = payload;
  const matchIgnoredStatuses = selectMatchIgnoreStatuses(storeState, fundingMatchId);

  if (matchIgnoredStatuses.includes(status)) {
    return;
  }

  try {
    const matchResponse = yield call(
      authApiService.get,
      routerService.generate('api.get_funding_match', {
        fundingMatchId,
      }),
    );

    const match = matchResponse.data;

    if (match?.offer?.latest) {
      // TODO: Need to be extracted to the separate saga or API should include the offer
      // There is no task for the backend yet
      const offerResponse = yield call(
        authApiService.get,
        routerService.generate('api.platform.funding_matches_offers', {
          matchId: fundingMatchId,
        }),
      );

      match.offer = offerResponse.data;
    }

    yield put(deleteFetchingOfferMatchId(fundingMatchId));
    yield put(sseUpdateFundingMatchSuccess(match, payload));
  } catch (error) {
    logger.error('Failed to request cancel funding match with the following error', error);

    yield put(sseUpdateFundingMatchFailure(error));
  }
}

export function* requestUpdateMatchStatus({ payload }) {
  const { status, fundingMatchId } = payload;
  const { incomplete, lenderRejected } = FundingMatchStatus;
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');
  const awaitingTime = 30000;

  try {
    const response = yield call(
      authApiService.post,
      routerService.generate('api.funding_match_application', {
        fundingMatchId,
      }),
    );

    yield delay(awaitingTime);
    yield put(setIgnoredMatchStatuses(fundingMatchId, []));

    const state = yield select(getState);
    const targetMatch = selectMatchFromFundingSearch(state, payload);
    const fetchingOfferMatchIds = makeSelectFetchingOfferMatchIds()(state);
    const isSSEStatusUpdated = targetMatch.status !== status;

    const isIncompleted = status === incomplete;
    const isRejected = status === lenderRejected && fetchingOfferMatchIds.includes(fundingMatchId);
    const isNotUpdated = !isSSEStatusUpdated && (isRejected || isIncompleted);

    if (isNotUpdated) {
      yield put(deleteFetchingOfferMatchId(fundingMatchId));
      yield put(requestUpdateMatchStatusSuccess(response.data, payload));
    }
  } catch (error) {
    logger.error('Failed to request update match status with the following error', error);

    yield put(requestUpdateMatchStatusFailure(error));
  }
}

export default function* fundingSearchMatchSaga() {
  return yield all([
    takeLatest(cancelFundingSearchMatch.request, requestCancelFundingSearchMatchSaga),
    takeLatest(fundingMatchStatus.request, requestUpdateMatchStatus),
    takeLatest(SSE_UPDATE_FUNDING_MATCH, requestMatchUpdateSaga),
  ]);
}
